/**
 * ChatsService — conversaciones de la vista chat, guardadas en localStorage.
 *
 * Cada usuario tiene su propia clave (`kb_chats_<id>`): en una PC compartida un
 * usuario no ve las conversaciones de otro. Solo viven en este navegador.
 *
 * Chat: { id, titulo, categoriaId, creado, actualizado, mensajes: [{ role, content, fuentesDetalle?, correccion?, sugerencias? }] }
 */
const PREFIX = "kb_chats_";
const MAX_CHATS = 30;
const MAX_MENSAJES = 60;
const TITULO_MAX = 60;

function storageKey(userId) {
  return PREFIX + (userId || "anon");
}

function leer(userId) {
  try {
    const list = JSON.parse(localStorage.getItem(storageKey(userId)));
    return Array.isArray(list) ? list : [];
  } catch { return []; }
}

// Si el navegador se queda sin espacio, se descartan las conversaciones más viejas
function escribir(userId, list) {
  let items = list.slice(0, MAX_CHATS);
  while (items.length) {
    try {
      localStorage.setItem(storageKey(userId), JSON.stringify(items));
      return true;
    } catch {
      items = items.slice(0, -1);
    }
  }
  try { localStorage.removeItem(storageKey(userId)); } catch {}
  return false;
}

/** Título de la conversación a partir de la primera pregunta. */
export function tituloDesde(pregunta) {
  const texto = String(pregunta || "").replace(/\s+/g, " ").trim();
  if (!texto) return "Nueva conversación";
  return texto.length > TITULO_MAX ? texto.slice(0, TITULO_MAX - 1).trimEnd() + "…" : texto;
}

/** Historial para /kb/ask: solo rol y contenido, últimos 10 turnos. */
export function historialParaApi(chat) {
  return (chat?.mensajes || [])
    .filter(m => (m.role === "user" || m.role === "assistant") && m.content?.trim())
    .map(m => ({ role: m.role, content: m.content }))
    .slice(-10);
}

export const ChatsService = {
  /** Conversaciones del usuario, la más reciente primero. */
  list(userId) {
    return leer(userId).sort((a, b) => String(b.actualizado).localeCompare(String(a.actualizado)));
  },

  get(userId, chatId) {
    return leer(userId).find(c => c.id === chatId) || null;
  },

  create({ categoriaId = null } = {}) {
    const ahora = new Date().toISOString();
    return {
      id: `ch_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`,
      titulo: "Nueva conversación",
      categoriaId,
      creado: ahora,
      actualizado: ahora,
      mensajes: [],
    };
  },

  /** Guarda (alta o actualización) y la deja primera en la lista. Sin mensajes no se guarda. */
  save(userId, chat) {
    if (!chat?.mensajes?.length) return false;
    const guardado = {
      ...chat,
      actualizado: new Date().toISOString(),
      mensajes: chat.mensajes.slice(-MAX_MENSAJES),
    };
    const resto = leer(userId).filter(c => c.id !== chat.id);
    return escribir(userId, [guardado, ...resto]);
  },

  remove(userId, chatId) {
    escribir(userId, leer(userId).filter(c => c.id !== chatId));
  },

  clear(userId) {
    try { localStorage.removeItem(storageKey(userId)); } catch {}
  },
};
